export interface SubscriptionRequest {
  companyName: string;
  companyEmail: string;
  companyPhone?: string;
  website?: string;
  planId: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerPhone?: string;
  trialDays?: number;
  config?: Record<string, any>;
}

export interface SubscriptionResponse {
  statusCode: number;
  body: {
    success: boolean;
    data?: {
      company: {
        id: string;
        slug: string;
        name: string;
        email: string;
        phone?: string;
        website?: string;
        plan_id: string;
        trial_ends_at?: string;
        created_at: string;
      };
      user: {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        phone?: string;
        role: string;
      };
      auth?: {
        token: string;
        refresh_token: string;
        expires_in: number;
      };
    };
    message?: string;
    error?: string;
  };
}

class SubscriptionService {
  private baseURL = import.meta.env.VITE_CORE_API_URL;

  /**
   * Registra una nueva empresa con su usuario propietario
   */
  async subscribeCompany(
    data: SubscriptionRequest,
  ): Promise<SubscriptionResponse> {
    try {
      console.log("🚀 Enviando registro de empresa...", {
        companyName: data.companyName,
        planId: data.planId,
        ownerEmail: data.ownerEmail,
      });

      if (!data.companyName || !data.companyEmail) {
        throw new Error("Nombre y email de la empresa son requeridos");
      }

      if (
        !data.ownerFirstName ||
        !data.ownerLastName ||
        !data.ownerEmail ||
        !data.ownerPassword
      ) {
        throw new Error("Datos del propietario son requeridos");
      }

      if (!data.planId) {
        throw new Error("ID del plan es requerido");
      }

      const subscriptionData = {
        ...data,
        trialDays: data.trialDays || 14,
        config: data.config || {},
      };

      const response = await fetch(`${this.baseURL}/subscribe`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      });

      console.log(
        "📡 Respuesta del servidor:",
        response.status,
        response.statusText,
      );

      const result = await response.json();
      console.log("📦 Resultado del registro:", result);

      if (response.status === 201 || response.ok) {
        console.log("✅ Registro exitoso");

        const success =
          result.success || result.body?.success || response.status === 201;

        if (success) {
          return {
            statusCode: response.status,
            body: {
              success: true,
              data: result.data || result.body?.data,
              message: result.message || result.body?.message,
            },
          };
        } else {
          const errorMessage =
            result.message ||
            result.body?.message ||
            result.error ||
            "Error en el registro";
          throw new Error(errorMessage);
        }
      } else {
        const errorMessage =
          result.message ||
          result.body?.message ||
          result.error ||
          `Error HTTP ${response.status}`;
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Error en registro de empresa:", error);

      let errorMessage = "Error en el registro";

      if (error.name === "TypeError" && error.message.includes("network")) {
        errorMessage = "Error de conexión con el servidor";
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        statusCode: 500,
        body: {
          success: false,
          error: errorMessage,
        },
      };
    }
  }

  /**
   * Valida los datos de registro
   */
  validateSubscriptionData(data: SubscriptionRequest): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validar empresa
    if (!data.companyName?.trim()) {
      errors.push("El nombre de la empresa es requerido");
    } else if (data.companyName.length < 2) {
      errors.push("El nombre de la empresa debe tener al menos 2 caracteres");
    }

    if (!data.companyEmail?.trim()) {
      errors.push("El email de la empresa es requerido");
    } else if (!this.isValidEmail(data.companyEmail)) {
      errors.push("El email de la empresa no es válido");
    }

    // Validar propietario
    if (!data.ownerFirstName?.trim()) {
      errors.push("El nombre del propietario es requerido");
    }

    if (!data.ownerLastName?.trim()) {
      errors.push("El apellido del propietario es requerido");
    }

    if (!data.ownerEmail?.trim()) {
      errors.push("El email del propietario es requerido");
    } else if (!this.isValidEmail(data.ownerEmail)) {
      errors.push("El email del propietario no es válido");
    }

    if (!data.ownerPassword?.trim()) {
      errors.push("La contraseña es requerida");
    } else if (data.ownerPassword.length < 8) {
      errors.push("La contraseña debe tener al menos 8 caracteres");
    }

    // Validar plan
    if (!data.planId?.trim()) {
      errors.push("El ID del plan es requerido");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhone(phone: string): boolean {
    if (!phone) return true;
    const phoneRegex = /^[\d\s\-\+\(\)]{8,20}$/;
    return phoneRegex.test(phone);
  }
}

export const subscriptionService = new SubscriptionService();
